import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Divider,
  Tab,
  Tabs,
  Card,
  CardContent,
  Stack,
  Badge,
  useMediaQuery,
  useTheme,
  Snackbar,
  Alert,
  Checkbox,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  Psychology as AiIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Edit as EditIcon,
  Archive as ArchiveIcon,
  Reply as ReplyIcon,
  Flag as FlagIcon,
  Refresh as RefreshIcon,
  ArrowBack as BackIcon,
  Email as EmailIcon,
  AutoAwesome as AutoIcon,
  PendingActions as PendingIcon,
  TrendingUp as StatsIcon,
  RateReview as RateReviewIcon,
  People as PeopleIcon,
  LocalShipping as LocalShippingIcon,
  Forum as ForumIcon,
  Campaign as CampaignIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { aiMailApi, emailsApi } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import AiCategoryBadge from '../../components/ai/AiCategoryBadge';
import {
  Sync as SyncIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
} from '@mui/icons-material';
import AiReplyReviewDialog from '../../components/ai/AiReplyReviewDialog';

const ACTION_TYPE_CONFIG = {
  reply_draft: { label: 'Risposta AI', icon: ReplyIcon, color: 'primary' },
  auto_archive: { label: 'Auto-archiviata', icon: ArchiveIcon, color: 'warning' },
  flag: { label: 'Contrassegnata', icon: FlagIcon, color: 'error' },
  categorize: { label: 'Classificata', icon: ForumIcon, color: 'info' },
  auto_read: { label: 'Letta automaticamente', icon: EmailIcon, color: 'default' },
};

function StatCard({ title, value, icon: Icon, color = 'primary', subtitle }) {
  return (
    <Card variant="outlined" sx={{ flex: 1, minWidth: 140 }}>
      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Icon sx={{ color: `${color}.main`, fontSize: 28 }} />
          <Box>
            <Typography variant="h5" fontWeight={700} lineHeight={1}>
              {value}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {title}
            </Typography>
          </Box>
        </Stack>
        {subtitle && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

export default function AiDashboard() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [tab, setTab] = useState('_review'); // '_review', 'clienti', 'fornitori', 'comunicazioni', 'pubblicita'
  const [reviewDialog, setReviewDialog] = useState({ open: false, action: null });
  const [sinceDate, setSinceDate] = useState(null);
  const [page, setPage] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [selectedIds, setSelectedIds] = useState([]);

  // Tab definitions
  const CATEGORY_TABS = [
    { value: '_review', label: 'Da Revisionare', icon: <RateReviewIcon /> },
    { value: 'clienti', label: 'Clienti', icon: <PeopleIcon /> },
    { value: 'fornitori', label: 'Fornitori', icon: <LocalShippingIcon /> },
    { value: 'comunicazioni', label: 'Comunicazioni', icon: <ForumIcon /> },
    { value: 'pubblicita', label: 'Pubblicità', icon: <CampaignIcon /> },
  ];

  // Fetch stats
  const { data: statsData } = useQuery({
    queryKey: ['ai-dashboard-stats'],
    queryFn: () => aiMailApi.getDashboardStats(),
    refetchInterval: 60000,
    retry: 1,
    staleTime: 30000,
  });
  const stats = statsData?.data?.stats || {};

  // Fetch actions - for _review tab show all pending, for category tabs show all (pending first)
  const isPendingTab = tab === '_review';
  const PAGE_SIZE = 20;
  const { data: pendingData, isLoading: pendingLoading } = useQuery({
    queryKey: ['ai-pending-actions', tab, page],
    queryFn: () => aiMailApi.getPendingActions({
      status: isPendingTab ? 'pending' : 'all',
      category: isPendingTab ? undefined : tab,
      limit: PAGE_SIZE,
      page
    }),
    refetchInterval: 30000,
    retry: 1,
    staleTime: 15000,
    keepPreviousData: true,
  });
  const actions = pendingData?.data?.actions || [];
  const totalActions = pendingData?.data?.total || 0;
  const totalPages = Math.ceil(totalActions / PAGE_SIZE);

  // Sort: pending first, then by date desc (backend already sorts, but keep as safety)
  const sortedActions = actions;
  const pendingActionsInPage = sortedActions.filter(a => a.status === 'pending');

  // Count pending per category for badges
  const { data: pendingCountData } = useQuery({
    queryKey: ['ai-pending-by-category'],
    queryFn: async () => {
      const categories = ['clienti', 'fornitori', 'comunicazioni', 'pubblicita'];
      const counts = {};
      let total = 0;
      for (const cat of categories) {
        const res = await aiMailApi.getPendingActions({ status: 'pending', category: cat, limit: 0 });
        counts[cat] = res?.data?.total || 0;
        total += counts[cat];
      }
      counts._review = total;
      return counts;
    },
    refetchInterval: 30000,
    retry: 1,
    staleTime: 15000,
  });

  // Mutations
  const approveMutation = useMutation({
    mutationFn: ({ actionId, attachments, replyTo, cc }) => aiMailApi.approveAction(actionId, attachments, replyTo, cc),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-pending-actions'] });
      queryClient.invalidateQueries({ queryKey: ['ai-dashboard-stats'] });
      queryClient.invalidateQueries({ queryKey: ['ai-pending-count'] });
      queryClient.invalidateQueries({ queryKey: ['ai-pending-by-category'] });
    }
  });

  const rejectMutation = useMutation({
    mutationFn: (actionId) => aiMailApi.rejectAction(actionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-pending-actions'] });
      queryClient.invalidateQueries({ queryKey: ['ai-dashboard-stats'] });
      queryClient.invalidateQueries({ queryKey: ['ai-pending-count'] });
      queryClient.invalidateQueries({ queryKey: ['ai-pending-by-category'] });
    }
  });

  const bulkRejectMutation = useMutation({
    mutationFn: (actionIds) => aiMailApi.bulkReject(actionIds),
    onSuccess: (response) => {
      const count = response?.data?.count || selectedIds.length;
      setSnackbar({ open: true, message: `${count} azioni rifiutate`, severity: 'success' });
      setSelectedIds([]);
      queryClient.invalidateQueries({ queryKey: ['ai-pending-actions'] });
      queryClient.invalidateQueries({ queryKey: ['ai-dashboard-stats'] });
      queryClient.invalidateQueries({ queryKey: ['ai-pending-count'] });
      queryClient.invalidateQueries({ queryKey: ['ai-pending-by-category'] });
    },
    onError: (err) => {
      setSnackbar({ open: true, message: 'Errore: ' + (err?.response?.data?.error || err.message), severity: 'error' });
    }
  });

  const editDraftMutation = useMutation({
    mutationFn: ({ actionId, data }) => aiMailApi.editActionDraft(actionId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-pending-actions'] });
    }
  });

  const syncAllMutation = useMutation({
    mutationFn: () => emailsApi.syncAll(),
    onSuccess: (response) => {
      const msg = response?.data?.message || 'Sincronizzazione completata';
      setSnackbar({ open: true, message: msg, severity: 'success' });
      queryClient.invalidateQueries({ queryKey: ['ai-dashboard-stats'] });
      queryClient.invalidateQueries({ queryKey: ['ai-pending-actions'] });
      queryClient.invalidateQueries({ queryKey: ['ai-pending-by-category'] });
    },
    onError: (err) => {
      setSnackbar({ open: true, message: 'Errore sync: ' + (err?.response?.data?.error || err.message), severity: 'error' });
    }
  });

  const triggerAnalysisMutation = useMutation({
    mutationFn: () => {
      const params = sinceDate ? { sinceDate: sinceDate.toISOString() } : {};
      return aiMailApi.triggerAnalysis(params);
    },
    onSuccess: (response) => {
      const msg = response?.data?.message || 'Analisi completata';
      setSnackbar({ open: true, message: msg, severity: 'success' });
      queryClient.invalidateQueries({ queryKey: ['ai-dashboard-stats'] });
      queryClient.invalidateQueries({ queryKey: ['ai-pending-actions'] });
      queryClient.invalidateQueries({ queryKey: ['ai-pending-count'] });
      queryClient.invalidateQueries({ queryKey: ['ai-pending-by-category'] });
    },
    onError: (err) => {
      setSnackbar({ open: true, message: err?.response?.data?.error || 'Errore durante l\'analisi', severity: 'error' });
    }
  });

  const handleApprove = async (actionId, attachments, replyTo, cc) => {
    await approveMutation.mutateAsync({ actionId, attachments, replyTo, cc });
  };

  const handleReject = async (actionId) => {
    await rejectMutation.mutateAsync(actionId);
  };

  const handleBulkReject = async () => {
    if (selectedIds.length === 0) return;
    await bulkRejectMutation.mutateAsync(selectedIds);
  };

  const toggleSelect = (actionId) => {
    setSelectedIds(prev => prev.includes(actionId) ? prev.filter(id => id !== actionId) : [...prev, actionId]);
  };

  const handleEditDraft = async (actionId, data) => {
    await editDraftMutation.mutateAsync({ actionId, data });
  };

  const handleRegenerate = async (actionId, customPrompt) => {
    const response = await aiMailApi.regenerateReply(actionId, customPrompt);
    const result = response?.data;
    if (result?.success) {
      queryClient.invalidateQueries({ queryKey: ['ai-pending-actions'] });
      // Update the action in the dialog with the new draft
      if (result.action) {
        setReviewDialog(prev => prev.open ? { ...prev, action: { ...prev.action, reply_draft: result.reply_draft } } : prev);
      }
    }
    return result;
  };

  const handleReanalyze = async (actionId) => {
    const response = await aiMailApi.reanalyzeAction(actionId);
    const result = response?.data;
    if (result?.success) {
      queryClient.invalidateQueries({ queryKey: ['ai-pending-actions'] });
      queryClient.invalidateQueries({ queryKey: ['ai-dashboard-stats'] });
      // Update dialog with the fresh action data
      if (result.action) {
        setReviewDialog(prev => prev.open ? { ...prev, action: result.action } : prev);
      }
    }
    return result;
  };

  const handleReviewAction = (action) => {
    if (action.action_type === 'reply_draft') {
      setReviewDialog({ open: true, action });
    }
  };

  const handleQuickApprove = async (action) => {
    if (action.action_type === 'reply_draft') {
      setReviewDialog({ open: true, action });
    } else {
      await handleApprove(action.id);
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    try {
      return format(new Date(date), 'dd MMM HH:mm', { locale: it });
    } catch {
      return '-';
    }
  };

  const getStatusChip = (status) => {
    const configs = {
      pending: { label: 'In attesa', color: 'warning' },
      approved: { label: 'Approvata', color: 'success' },
      rejected: { label: 'Rifiutata', color: 'error' },
      auto_applied: { label: 'Automatica', color: 'info' },
      error: { label: 'Errore', color: 'error' },
    };
    const c = configs[status] || { label: status, color: 'default' };
    return <Chip label={c.label} color={c.color} size="small" variant="outlined" />;
  };

  return (
    <Box sx={{ p: isMobile ? 1 : 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate('/crm/email/inbox')}
          size="small"
        >
          Inbox
        </Button>
        <AiIcon color="primary" sx={{ fontSize: 32 }} />
        <Typography variant="h5" fontWeight={700} sx={{ flex: 1 }}>
          AI Email Assistant
        </Typography>
        <DatePicker
          label="Analizza dal"
          value={sinceDate}
          onChange={(newValue) => setSinceDate(newValue)}
          slotProps={{
            textField: { size: 'small', sx: { width: 180 } },
            actionBar: { actions: ['clear', 'today'] }
          }}
          format="dd/MM/yyyy"
        />
        <Button
          startIcon={syncAllMutation.isPending ? <CircularProgress size={16} /> : <SyncIcon />}
          onClick={() => syncAllMutation.mutate()}
          disabled={syncAllMutation.isPending}
          size="small"
          variant="outlined"
          color="secondary"
        >
          {syncAllMutation.isPending ? 'Sync...' : 'Sincronizza Email'}
        </Button>
        <Button
          startIcon={triggerAnalysisMutation.isPending ? <CircularProgress size={16} /> : <AutoIcon />}
          onClick={() => triggerAnalysisMutation.mutate()}
          disabled={triggerAnalysisMutation.isPending}
          size="small"
          variant="contained"
          color="primary"
        >
          {triggerAnalysisMutation.isPending ? 'Analisi in corso...' : 'Analizza Email'}
        </Button>
        <Button
          startIcon={<RefreshIcon />}
          onClick={() => {
            queryClient.invalidateQueries({ queryKey: ['ai-dashboard-stats'] });
            queryClient.invalidateQueries({ queryKey: ['ai-pending-actions'] });
          }}
          size="small"
          variant="outlined"
        >
          Aggiorna
        </Button>
      </Box>

      {/* Stats Cards */}
      <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <StatCard
          title="In attesa di review"
          value={stats.pending || 0}
          icon={PendingIcon}
          color="warning"
        />
        <StatCard
          title="Auto-applicate oggi"
          value={stats.auto_applied_today || 0}
          icon={AutoIcon}
          color="info"
        />
        <StatCard
          title="Approvate oggi"
          value={stats.approved_today || 0}
          icon={ApproveIcon}
          color="success"
        />
        <StatCard
          title="Email analizzate"
          value={stats.analyzed_total || 0}
          icon={StatsIcon}
          color="primary"
          subtitle={
            stats.by_category
              ? Object.entries(stats.by_category).map(([k, v]) => `${k}: ${v}`).join(', ')
              : undefined
          }
        />
      </Stack>

      {/* Category Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => { setTab(v); setPage(0); setSelectedIds([]); }}
          variant="scrollable"
          scrollButtons="auto"
        >
          {CATEGORY_TABS.map((t) => {
            const count = pendingCountData?.[t.value] || 0;
            return (
              <Tab
                key={t.value}
                value={t.value}
                icon={t.icon}
                iconPosition="start"
                label={
                  count > 0
                    ? <Badge badgeContent={count} color="error" sx={{ '& .MuiBadge-badge': { right: -12, top: 2 } }}>{t.label}</Badge>
                    : t.label
                }
                sx={{ minHeight: 48, textTransform: 'none', fontSize: '0.85rem' }}
              />
            );
          })}
        </Tabs>
      </Paper>

      {/* Actions List */}
      <Paper>
        {/* Bulk selection toolbar */}
        {selectedIds.length > 0 && (
          <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 2, bgcolor: 'primary.50', borderBottom: '1px solid', borderColor: 'divider' }}>
            <Checkbox
              indeterminate={selectedIds.length > 0 && selectedIds.length < pendingActionsInPage.length}
              checked={pendingActionsInPage.length > 0 && selectedIds.length === pendingActionsInPage.length}
              onChange={(e) => {
                if (e.target.checked) {
                  setSelectedIds(pendingActionsInPage.map(a => a.id));
                } else {
                  setSelectedIds([]);
                }
              }}
              size="small"
            />
            <Typography variant="body2" fontWeight={600}>
              {selectedIds.length} selezionat{selectedIds.length === 1 ? 'a' : 'e'}
            </Typography>
            <Button
              variant="contained"
              color="error"
              size="small"
              startIcon={<RejectIcon />}
              onClick={handleBulkReject}
              disabled={bulkRejectMutation.isPending}
            >
              {bulkRejectMutation.isPending ? 'Rifiuto...' : `Rifiuta ${selectedIds.length}`}
            </Button>
            <Button size="small" onClick={() => setSelectedIds([])}>
              Annulla
            </Button>
          </Box>
        )}
        {pendingLoading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : sortedActions.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <AiIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">
              {isPendingTab ? 'Nessuna azione in attesa di review' : `Nessuna email in ${CATEGORY_TABS.find(t => t.value === tab)?.label || tab}`}
            </Typography>
            {isPendingTab && (
              <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>
                Le email verranno analizzate automaticamente dopo la sincronizzazione IMAP
              </Typography>
            )}
          </Box>
        ) : (
          sortedActions.map((action, idx) => {
            const email = action.email || {};
            const config = ACTION_TYPE_CONFIG[action.action_type] || ACTION_TYPE_CONFIG.flag;
            const ActionIcon = config.icon;
            const isReviewed = action.status !== 'pending';

            return (
              <Box key={action.id}>
                {idx > 0 && <Divider />}
                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    flexWrap: 'wrap',
                    opacity: isReviewed ? 0.6 : 1,
                    borderLeft: isReviewed ? '3px solid transparent' : '3px solid',
                    borderLeftColor: isReviewed ? 'transparent' : 'primary.main',
                    '&:hover': { bgcolor: 'action.hover', opacity: 1 },
                    cursor: action.action_type === 'reply_draft' && action.status === 'pending' ? 'pointer' : 'default',
                  }}
                  onClick={() => action.status === 'pending' && handleReviewAction(action)}
                >
                  {/* Checkbox for pending items */}
                  {!isReviewed && (
                    <Checkbox
                      size="small"
                      checked={selectedIds.includes(action.id)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggleSelect(action.id)}
                      sx={{ p: 0 }}
                    />
                  )}

                  {/* Reviewed indicator */}
                  {isReviewed ? (
                    <Tooltip title="Revisionata">
                      <CheckCircleOutlineIcon color="success" sx={{ fontSize: 20, opacity: 0.7 }} />
                    </Tooltip>
                  ) : (
                    <Tooltip title={config.label}>
                      <ActionIcon color={config.color} />
                    </Tooltip>
                  )}

                  {/* Email info */}
                  <Box sx={{ flex: 1, minWidth: 200 }}>
                    <Typography variant="body2" fontWeight={isReviewed ? 'normal' : 600} noWrap>
                      {email.from_name || email.from_address || 'Email sconosciuta'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {email.subject || '(nessun oggetto)'}
                    </Typography>
                  </Box>

                  {/* Category badge */}
                  {action.ai_category && (
                    <AiCategoryBadge
                      category={action.ai_category}
                      confidence={action.ai_confidence}
                      size="small"
                    />
                  )}

                  {/* Status chip for reviewed items */}
                  {isReviewed && getStatusChip(action.status)}

                  {/* Date */}
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 80, textAlign: 'right' }}>
                    {formatDate(email.date_sent || action.created_at)}
                  </Typography>

                  {/* Action buttons (only for pending) */}
                  {action.status === 'pending' && (
                    <Stack direction="row" spacing={0.5} onClick={(e) => e.stopPropagation()}>
                      {action.action_type === 'reply_draft' && (
                        <Tooltip title="Rivedi e modifica">
                          <IconButton size="small" onClick={() => handleReviewAction(action)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Approva">
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => handleQuickApprove(action)}
                          disabled={approveMutation.isPending}
                        >
                          <ApproveIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Rifiuta">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleReject(action.id)}
                          disabled={rejectMutation.isPending}
                        >
                          <RejectIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  )}

                  {/* View email button */}
                  <Tooltip title="Vedi email">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/crm/email/${email.id}`);
                      }}
                    >
                      <EmailIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            );
          })
        )}
      </Paper>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mt: 2 }}>
          <Button
            startIcon={<PrevIcon />}
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
            size="small"
            variant="outlined"
          >
            Precedente
          </Button>
          <Typography variant="body2" color="text.secondary">
            Pagina {page + 1} di {totalPages} ({totalActions} totali)
          </Typography>
          <Button
            endIcon={<NextIcon />}
            onClick={() => setPage(p => p + 1)}
            disabled={page >= totalPages - 1}
            size="small"
            variant="outlined"
          >
            Successiva
          </Button>
        </Box>
      )}

      {/* Reply Review Dialog */}
      <AiReplyReviewDialog
        open={reviewDialog.open}
        action={reviewDialog.action}
        onClose={() => setReviewDialog({ open: false, action: null })}
        onApprove={handleApprove}
        onReject={handleReject}
        onEditDraft={handleEditDraft}
        onRegenerate={handleRegenerate}
        onReanalyze={handleReanalyze}
      />

      {/* Snackbar feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
